import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Award, Bug } from "lucide-react";

const Counter = ({ target, format, suffix = "", testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, target, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, target]);

  return (
    <p ref={ref} data-testid={testid} className="stats-number font-serif text-5xl lg:text-6xl font-bold tracking-tight text-moss">
      {format(value)}
      {suffix}
    </p>
  );
};

const STATS = [
  {
    icon: Award,
    target: 10,
    suffix: "+",
    format: (v) => v.toString(),
    label: "Anni di esperienza",
    testid: "stat-counter-anni",
  },
  {
    icon: Bug,
    target: 1000,
    suffix: "+",
    format: (v) => (v >= 1000 ? "1.000" : v.toString()),
    label: "Interventi di disinfestazione eseguiti sul campo",
    testid: "stat-counter-interventi",
  },
];

export const StatsSection = () => (
  <section id="numeri-esperienza" data-testid="stats-section" className="stats-section bg-tint border-y border-line py-20 lg:py-24">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="text-center max-w-2xl mx-auto"
      >
        <h2 className="stats-title font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug text-moss">
          I numeri della nostra esperienza
        </h2>
        <p className="stats-subtitle mt-4 text-base sm:text-lg text-sub">
          Esperienza sul campo, supporto concreto e soluzioni accessibili per risolvere
          le infestazioni in modo efficace.
        </p>
      </motion.div>

      <div className="stats-grid mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 max-w-3xl mx-auto">
        {STATS.map(({ icon: Icon, target, suffix, format, label, testid }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="stats-card rounded-3xl bg-white border border-line p-7 lg:p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(27,59,43,0.10)]"
          >
            <div className="stats-icon mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-sage mb-5">
              <Icon size={20} className="text-moss" />
            </div>
            <Counter target={target} suffix={suffix} format={format} testid={testid} />
            <p className="stats-label mt-3 text-sm leading-snug text-sub">
              {label}
            </p>
          </motion.div>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="stats-summary mt-12 max-w-3xl mx-auto text-center text-base leading-relaxed text-ink"
      >
        Oltre 10 anni di esperienza e più di 1.000 interventi reali sul campo. Oggi tutta
        questa esperienza è al tuo servizio per aiutarti a risolvere le infestazioni in
        modo pratico, guidato ed efficace.
      </motion.p>
    </div>
  </section>
);
